import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Table,
  Tag,
  Typography,
  Space,
  Button,
  Tooltip,
  Empty,
  Input,
} from 'antd';
import { EyeOutlined, EditOutlined, WarningOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import axios from 'axios';
import { useSelector, useDispatch } from 'react-redux';
import { setMessage } from '../../../redux/message.reducer';
import './ActionRequired.css';

const { Title } = Typography;

const STATUS_COLORS = {
  open: 'blue',
  pending: 'orange',
  ready: 'green',
  repair: 'magenta',
  dispatch: 'cyan',
  completed: 'green',
  cancelled: 'red',
  return: 'purple',
};

const formatDate = (value) => {
  if (!value) return '—';
  const d = dayjs(value);
  return d.isValid() ? d.format('MMM D, YYYY') : '—';
};

const daysOverdue = (targetDate) => {
  if (!targetDate) return 0;
  const target = dayjs(targetDate).startOf('day');
  const today = dayjs().startOf('day');
  return today.diff(target, 'day');
};

export default function ActionRequired() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.authReducer);

  const getDefaultStatusFilter = () => {
    const roleValues = user?.roles?.map((r) => r.value) ?? [];
    if(user?.roles?.some((r) => r.value === 'admin' || r.value === 'manager' || r.value === 'read-only')) {
      return undefined;
    }
    if (roleValues.includes('stock-manager')) {
      return ['dispatch'];
    } else if (roleValues.some((v) => v === 'order-manager' || v === 'stone-manager')) {
      return ['open', 'pending'];
    } else if (roleValues.includes('qc-manager')) {
      return ['ready'];
    }
  };

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [statusFilter] = useState(() => getDefaultStatusFilter());

  const fetchActionRequired = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter?.length) {
        params.status = Array.isArray(statusFilter) ? statusFilter.join(',') : statusFilter;
      }
      const res = await axios.get('/orders/action-required', { params });
      setOrders(res.data?.data ?? []);
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to load action required orders';
      dispatch(setMessage({ message: msg, type: 'error' }));
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [dispatch, statusFilter]);

  useEffect(() => {
    fetchActionRequired();
  }, [fetchActionRequired]);

  const filteredOrders = React.useMemo(() => {
    if (!searchInput.trim()) return orders;
    const q = searchInput.trim().toLowerCase();
    return orders.filter(
      (o) =>
        (o.orderNumber && String(o.orderNumber).toLowerCase().includes(q)) ||
        (o.customerName && String(o.customerName).toLowerCase().includes(q)) ||
        (o.sourceOfOrder && String(o.sourceOfOrder).toLowerCase().includes(q)) ||
        (o.status && String(o.status).toLowerCase().includes(q))
    );
  }, [orders, searchInput]);

  const columns = [
    {
      title: 'Order #',
      dataIndex: 'orderNumber',
      key: 'orderNumber',
      width: 120,
      render: (val, record) => (
        <span
          className="action-required-cell-link"
          role="button"
          tabIndex={0}
          onClick={() => navigate(`/orders/${record._id}`)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              navigate(`/orders/${record._id}`);
            }
          }}
        >
          {val || '—'}
        </span>
      ),
    },
    {
      title: 'Customer',
      dataIndex: 'customerName',
      key: 'customerName',
      render: (val) => <span className="action-required-cell-customer">{val || '—'}</span>,
    },
    {
      title: 'Order date',
      dataIndex: 'orderDate',
      key: 'orderDate',
      width: 110,
      render: formatDate,
    },
    {
      title: 'Target date',
      dataIndex: 'targetDate',
      key: 'targetDate',
      width: 110,
      render: (val) => <span className="action-required-date-overdue">{formatDate(val)}</span>,
    },
    {
      title: 'Overdue',
      key: 'overdue',
      width: 90,
      render: (_, record) => {
        const days = daysOverdue(record.targetDate);
        return (
          <Tag color="red" className="action-required-overdue-tag">
            {days} {days === 1 ? 'day' : 'days'}
          </Tag>
        );
      },
    },
    {
      title: 'Source',
      dataIndex: 'sourceOfOrder',
      key: 'sourceOfOrder',
      width: 100,
      render: (s) => <Tag className="action-required-tag-source">{s || '—'}</Tag>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (s) => (
        <Tag color={STATUS_COLORS[s] || 'default'} className="action-required-tag-status">
          {s === 'ready' ? 'Ready (send to QC)' : (s ? String(s).charAt(0).toUpperCase() + String(s).slice(1) : '—')}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="View">
            <Button
              type="text"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => navigate(`/orders/${record._id}`)}
            />
          </Tooltip>
          <Tooltip title="Edit">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => navigate(`/orders/${record._id}/edit`)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div className="action-required-page">
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <div className="action-required-header">
          <div className="action-required-header-left">
            <div className="action-required-header-title-row">
              <WarningOutlined className="action-required-header-icon" />
              <Title level={3} style={{ margin: 0 }}>
                Action Required
              </Title>
              {!loading && orders.length > 0 && (
                <Tag color="orange" className="action-required-count-badge">
                  {orders.length} order{orders.length !== 1 ? 's' : ''}
                </Tag>
              )}
            </div>
            <Input
              placeholder="Search by order #, customer, source, or status"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              allowClear
              className="action-required-search-input"
            />
          </div>
          <p className="action-required-subtitle">
            Orders past target date that are not yet ready or dispatched. Review and update status as needed.
          </p>
        </div>

        <Card className="action-required-card">
          <Table
            className="action-required-table"
            columns={columns}
            dataSource={filteredOrders.map((row) => ({ ...row, key: row._id }))}
            rowClassName="action-required-row"
            loading={loading}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} orders`,
              size: 'default',
              pageSizeOptions: ['10', '20', '50'],
            }}
            size="middle"
            tableLayout="auto"
            scroll={{ x: 900 }}
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="No orders require action. You're all caught up!"
                />
              ),
            }}
          />
        </Card>
      </Space>
    </div>
  );
}
